// (C) 2022 GoodData Corporation
import { invariant } from "ts-invariant";
import { FormatterUsingIcu, ResolutionContext } from "./fmt_base.js";
import { FormatChar, FormatString, FractionFormatStats, FractionPartFormatter, WholePartFormatter, } from "./fmt_number_commons.js";
import { analyzeVisualTags, DigitCounter, ThousandSeparatorMatcher, ValueScalingCounter, } from "./fmt_number_compile.js";
/**
 * A class representing the ICU format constants.
 */
class IcuFormatConstants {
    constructor(options) {
        this.thousandsSeparator = options.thousandsSeparator || IcuFormatConstants.DefaultThousandSeparator;
        this.decimalSeparator = options.decimalSeparator || IcuFormatConstants.DefaultDecimalSeparator;
        this.nanString = options.nanString || IcuFormatConstants.DefaultNaNString;
        this.positiveInfinity = options.positiveInfinity || IcuFormatConstants.DefaultPositiveInfinity;
        this.negativeInfinity = options.negativeInfinity || IcuFormatConstants.DefaultNegativeInfinity;
    }
}
IcuFormatConstants.DefaultThousandSeparator = ",";
IcuFormatConstants.DefaultDecimalSeparator = ".";
IcuFormatConstants.DefaultNaNString = "NaN";
IcuFormatConstants.DefaultPositiveInfinity = "Infinity";
IcuFormatConstants.DefaultNegativeInfinity = "-Infinity";
export { IcuFormatConstants };
/**
 * A 'compiled' number format string - implementation that almost entirely (sans one or two documented corner cases/bugs)
 * matches the reference implementation. The raw format string is compiled into this representation that exposes
 * metadata and methods essential to perform the formatting task.
 *
 * Indeed, this whole thing could be further decomposed into smaller classes and perhaps sprinkled with a few
 * patterns to make the formatting more object oriented and have smaller focused classes. Not doing that for now;
 * maybe could be undertaken once the entire implementation is ported here.
 */
export class NumberFormat extends FormatterUsingIcu {
    constructor(effectivelyEmpty, escapedFormat, visualPart, escapedFormatNoVisuals, wholePart, fractionPart, colorPlaceholderValues, colors, percent, scaling, thousandsSeparators, hasDecimal) {
        super();
        this.effectivelyEmpty = effectivelyEmpty;
        this.escapedFormat = escapedFormat;
        this.visualPart = visualPart;
        this.escapedFormatNoVisuals = escapedFormatNoVisuals;
        this.wholePart = wholePart;
        this.fractionPart = fractionPart;
        this.colorPlaceholderValues = colorPlaceholderValues;
        this.colors = colors;
        this.percent = percent;
        this.scaling = scaling;
        this.thousandsSeparators = thousandsSeparators;
        this.hasDecimal = hasDecimal;
    }
    static doubleToString(value, precision) {
        //check negative 0
        const isNegativeZero = Object.is(value, -0);
        const stringValue = value.toFixed(precision);
        return isNegativeZero ? "-" + stringValue : stringValue;
    }
    static leftPad(val, size, ch) {
        return String(val).padStart(size, ch);
    }
    static roundInternal(number, decimals) {
        //this implementation is ported from the reference number.js implementation
        if (decimals > 0) {
            // decimalPrecision variable is an ugly hack to limit toFixed() parameter to 20, which is ECMAScript maximum supported range
            // numbers like 0.54999999999999999, with 0.0 format, will still be rounded to 0.6, since there is simply not enough precision
            const decimalPrecision = Math.min(decimals + 10, 20);
            const regexp = new RegExp("(-?\\d*)\\.(\\d{" + decimals + "})(\\d)\\d*$");
            const m = number.toFixed(decimalPrecision).match(regexp);
            if (m === null || m === void 0 ? void 0 : m.length) {
                const carryover = m[1].charAt(0) === "-" ? -1 : 1; // negative number carryovers need to be negative, too
                const s = NumberFormat.leftPad(Math.round((m[2] + "." + m[3])), decimals, "0");
                return s.length > decimals ? Number(m[1]) + carryover : Number(m[1] + "." + s);
            }
        }
        return number;
    }
    static round(number, precision) {
        if (precision === 0) {
            // If there is no decimal point, round to nearest integer
            // rounding half AWAY from zero, as in Excel (as opposed to Math.round())
            const sign = number < 0 ? -1 : 1;
            return Math.round(number * sign) * sign;
        }
        else {
            return NumberFormat.roundInternal(number, precision);
        }
    }
    static numberHasValue(value) {
        return value !== null && value !== undefined;
    }
    /*
     * Modifies the input according to the percent formatting and scaling and prepares
     * string representations of whole and fraction parts of the number to actually format. The code mimics the logic in
     * the original implementation and has some amount of weirdness.
     *
     * The input is first modified as follows:
     *
     * - for percent value formatting, multiply value by 100
     * - value scaling is applied (e.g. dividing by 1000 for each scaling factor)
     * - finally, the value is rounded up to the precision desired as indicated by the fraction part
     *   of the format string; this is done also for 0 precision and so things like 4.5 are then formatted as 6
     *
     * Then the value gets converted to standard string and split at decimal point into whole and fraction parts. There
     * is some final funny stuff ported from the original implementation: if the right side has no digit precision (e.g. there
     * is no `0` placeholder), then if the whole part is "0" or "-0" then it should be converted to "" or "-" respectively.
     *
     * Not sure why that has to be. Essentially, it leads to format string 'x.##' and value of -0.1 to be formatted as "x-.1";
     * plus of course there are tons of other scenarios where this also hits and results in questionable output.
     * See: https://github.com/gooddata/gdc-numberjs/blob/0ab90ee596a854b8ad38fa7956d7148b8c1291df/lib/number.js#L509
     *
     * NOTE:
     * for scaling, going with incremental divisions instead of using pow as the reference impl did
     * see: https://baptiste-wicht.com/posts/2017/09/cpp11-performance-tip-when-to-use-std-pow.html
     */
    prepareValueToFormat(input) {
        let valueToFormat = input;
        if (this.percent) {
            valueToFormat *= 100;
        }
        for (let i = 0; i < this.scaling; i++) {
            valueToFormat /= 1000;
        }
        const precision = Math.min(20, this.fractionPart.getDigits());
        valueToFormat = NumberFormat.round(valueToFormat, precision);
        const numberString = NumberFormat.doubleToString(valueToFormat, precision);
        let wholePart = "";
        let fractionPart = "";
        if (numberString === "") {
            // this may happen for very large numbers that the DoubleToStringConverter cannot handle
            // the whole part has more than 60 digits, or fraction part has more than 100 digits
            return { valueToFormat, wholePart, fractionPart };
        }
        if (this.fractionPart.getDigits()) {
            const decimal = numberString.indexOf(".");
            // if this happens then the rounding code or float to string conversion is hosed; code is here because the
            // format string includes the fraction part which specifies desired precision; the number is rounded to that
            // precision and converted, so there must be the decimal separator
            invariant(decimal !== -1, "Decimal separator not found in the numberString.");
            wholePart = numberString.substring(0, decimal);
            fractionPart = numberString.substring(decimal + 1);
        }
        else {
            wholePart = numberString;
            fractionPart = "";
        }
        // WEIRD BEGIN  (see function-level comment)
        if (this.wholePart.getDigits() === 0) {
            if (wholePart === "0") {
                wholePart = "";
            }
            else if (wholePart === "-0") {
                if (!this.hasDecimal) {
                    wholePart = "";
                }
                else {
                    wholePart = "-";
                }
            }
        }
        else if (!this.hasDecimal && wholePart === "-0") {
            wholePart = "0";
        }
        // WEIRD END
        return { valueToFormat, wholePart, fractionPart };
    }
    isLiteral() {
        return this.wholePart.getPlacements() + this.fractionPart.getPlacements() === 0;
    }
    doFormat(ctx, number, metadata) {
        let result = "";
        if (number === null) {
            // follows reference impl - empty string is returned; mind that the reference impl was treating empty strings as NULL values
            // (documented in contract)
        }
        else if (!this.effectivelyEmpty && this.isLiteral()) {
            // format string (disregarding visuals) is not empty and contains a literal (no number placement characters) ->
            // return as-is including visuals
            //
            // this mimics the reference impl; mind that if the format string is empty, this branch is not
            // hit and formatting is done against the empty string
            result = ctx.options.includeColors ? this.escapedFormat : this.escapedFormatNoVisuals;
        }
        else {
            let numberToFormat = number;
            if (ctx.formatAbsoluteValue && NumberFormat.numberHasValue(number)) {
                numberToFormat = Math.abs(number);
            }
            const toFormat = this.prepareValueToFormat(numberToFormat);
            // if thousands separation is desired by the format string, then use the separator provided by caller;
            // otherwise use empty separator to indicate to the part formatter that no separation is needed
            // note: the caller may also pass an empty separator, this is valid and results in separators being added
            const thousandSeparator = this.thousandsSeparators
                ? ctx.constants.thousandsSeparator
                : WholePartFormatter.NoThousandsSeparators;
            // if visual formatting is desired, pass the pointer to the vector that contains values to substitute for color
            // placeholders; otherwise pass a null pointer -> substitution will yield no chars
            const colorTags = ctx.options.includeColors ? this.colorPlaceholderValues : undefined;
            const formattedWhole = this.wholePart.format(toFormat.wholePart, thousandSeparator, colorTags);
            const fractionStats = new FractionFormatStats();
            const formattedFraction = this.fractionPart.format(toFormat.fractionPart, fractionStats, colorTags);
            if (ctx.options.includeColors) {
                result += this.visualPart;
            }
            result += formattedWhole;
            if (formattedFraction.length) {
                // only add decimal separator if some digits were placed into the fraction part
                //
                // this hits in cases like "0.#### USD" when value has no fraction; fraction formatting has
                // logic to trim trailing zeroes if the number part of the format string trails with '#'
                //
                // without this code, on input 4, the result would be "4. USD"
                //
                // see original impl here:
                // https://github.com/gooddata/gdc-numberjs/blob/0ab90ee596a854b8ad38fa7956d7148b8c1291df/lib/number.js#L533
                //
                //
                // WEIRD
                //
                // the reference implementation (possibly due to a hole/bad regex) also adds a decimal point if no digits
                // were placed but some escaped characters were; so for instance "#.##\\ " (mind the space at the end), when
                // used to format 1.00 will result in "1. "
                //
                // that behavior borders on being a hole and is thus gated via the legacy compatibility option
                //
                // additionally, also due to a hole, the reference implementation adds a decimal point if any literal digits
                // (e.g. 1, 2 up to 9) were part of the fraction format string and were copied as-is into the result
                if (fractionStats.placedDigits > 0 ||
                    (ctx.options.legacyCompatibility && fractionStats.placedEscapedCharacters) ||
                    (ctx.options.legacyCompatibility && fractionStats.placedLiteralDigits)) {
                    result += ctx.constants.decimalSeparator;
                }
                result += formattedFraction;
            }
        }
        if (metadata !== undefined) {
            metadata.colors.color = this.colors.color;
            metadata.colors.customColor = this.colors.customColor;
            metadata.colors.customBgColor = this.colors.customBgColor;
            const resolved = this.doResolve(new ResolutionContext(ctx.options, ctx.formatAbsoluteValue), number);
            if (resolved !== null) {
                metadata.effectiveFormat = resolved;
            }
            else {
                metadata.effectiveFormat = undefined;
            }
        }
        return result;
    }
    doResolve(ctx, number) {
        // WEIRD
        //
        // when it comes to NULL, the reference implementation behaves differently when resolving
        // NULL against format strings that are used as alternative in a conditional and those that are not
        //
        // NULL vs any normal number format => must always return ""
        // NULL vs conditional format => always return format string; this is somewhat understandable
        // for alternatives that have [=null] condition. however, the same behavior is in effect also
        // for those conditions that in EcmaScript coerce and evaluate true. E.g. reference impl will
        // match null value against [>=0] condition. if that happens, the format value is also
        // returned as is
        //
        // That is weird behavior because in the end the format string for the [>=0] condition will be
        // evaluated and lead to same result as when not doing conditional formatting. So why the resolution
        // dichotomy?
        if (number === null && !ctx.nestedFormatter) {
            return "";
        }
        if (!ctx.options.includeColors) {
            return this.escapedFormatNoVisuals;
        }
        return this.escapedFormat;
    }
    /**
     * Factory method that compiles provided format string as a number formatter.
     *
     * The compilation will create two variants of the formatter - one compiled using the full format string,
     * other compiled for format string without all color tags stripped away. These are hidden behind Formatter
     * interface and one or the  other variant will be used depending on the options passed by the caller.
     *
     * @param matchers - regex matchers
     * @param formatString - formatString to compile
     * @returns always new instance
     */
    static compile(matchers, formatString) {
        const visualAnalysis = analyzeVisualTags(matchers, formatString);
        let escapedSubstitutedBareFormat = "";
        let escapedNoColorBareFormat = "";
        let percent = false;
        let decimal = false;
        let currentPart = 0;
        const parts = [new FormatString(), new FormatString()];
        const partSize = [0, 0];
        const digitCounters = [new DigitCounter(), new DigitCounter(true)];
        const placementChars = [0, 0];
        const thousandsMatcher = [
            new ThousandSeparatorMatcher(),
            new ThousandSeparatorMatcher(),
        ];
        const scalingCounter = [
            new ValueScalingCounter(),
            new ValueScalingCounter(true),
        ];
        const sunkenCommas = [[], []];
        let escape = false;
        for (let i = 0; i < visualAnalysis.bareFormatWithPlaceholders.length; i++) {
            const c = visualAnalysis.bareFormatWithPlaceholders[i];
            if (!escape && c === "\\") {
                escape = true;
                continue;
            }
            if (c === "\r" || c === "\n") {
                // completely ignore cr/lf
                // see: https://github.com/gooddata/gdc-numberjs/blob/0ab90ee596a854b8ad38fa7956d7148b8c1291df/lib/number.js#L845
                continue;
            }
            const fc = new FormatChar(c, escape);
            const placeholderIdx = fc.getColorPlaceholderIndex();
            escape = false;
            if (placeholderIdx >= 0) {
                escapedSubstitutedBareFormat += visualAnalysis.colorPlaceholderValues[placeholderIdx];
                if (currentPart === 0) {
                    // do revers at index
                    visualAnalysis.colorPlaceholderValues[placeholderIdx] =
                        visualAnalysis.colorPlaceholderValues[placeholderIdx].split("").reverse().join("");
                }
            }
            else {
                escapedSubstitutedBareFormat += c;
                escapedNoColorBareFormat += c;
            }
            if (fc.isDecimal()) {
                if (currentPart === 0) {
                    currentPart++;
                }
                decimal = true;
                continue;
            }
            if (fc.isPercent()) {
                percent = true;
            }
            if (fc.isComma()) {
                sunkenCommas[currentPart].push(parts[currentPart].length());
            }
            else {
                parts[currentPart].append(fc);
                partSize[currentPart]++;
            }
            thousandsMatcher[currentPart].onCharacter(fc);
            scalingCounter[currentPart].onCharacter(fc);
            if (fc.isPlacement()) {
                placementChars[currentPart]++;
            }
            if (!fc.isComma() || (currentPart === 1 && !partSize[1])) {
                digitCounters[currentPart].onCharacter(fc);
            }
        }
        if (escape) {
            parts[currentPart].append(new FormatChar("\\"));
            escapedSubstitutedBareFormat += "\\";
            escapedNoColorBareFormat += "\\";
        }
        const escapedFormat = visualAnalysis.visualPart + escapedSubstitutedBareFormat;
        const thousandSeparator = thousandsMatcher[0].isMatched() || thousandsMatcher[1].isMatched();
        const scaling = scalingCounter[0].scaling() + scalingCounter[1].scaling();
        for (let i = 0; i < 2; i++) {
            if (thousandSeparator || scalingCounter[i].scaling() !== 0) {
                continue;
            }
            for (let iter = sunkenCommas[i].length - 1; iter >= 0; --iter) {
                parts[i].insert(sunkenCommas[i][iter], new FormatChar(","));
            }
        }
        const wholePart = new WholePartFormatter(parts[0], digitCounters[0].count(), placementChars[0]);
        const fractionPart = new FractionPartFormatter(parts[1], digitCounters[1].count(), placementChars[1]);
        const compiledFormatString = new NumberFormat(escapedSubstitutedBareFormat.length === 0, escapedFormat, visualAnalysis.visualPart, escapedNoColorBareFormat, wholePart, fractionPart, visualAnalysis.colorPlaceholderValues, visualAnalysis.colors, percent, scaling, thousandSeparator, decimal);
        return compiledFormatString;
    }
}
//# sourceMappingURL=fmt_number.js.map